import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getKCUser } from '../../redux/App/App.slice';
import styled, { useTheme } from 'styled-components';
import AvatarComponent, { StyledAvatar } from '../Avatar.component';
import IconButtonComponent, { StyledIconButton } from '../IconButton.component';
import SearchIcon from '../Icons/Search.icon';
import SnowIcon from '../Icons/Snow.icon';
import BallIcon from '../Icons/Ball.icon';
import { StyledBaseIcon } from '../Icons/Base.icon';
import { useModalManager } from '../../hooks/ModalManager.hook';
import GlobalSearchModal from '../Modal/GlobalSearchModal';
import { getNotificationsCounter } from '../../redux/Notification/NotificationSlice';
import NotificationsModalComponent from '../Modal/Notifications/NotificationsModal.component';
import DropdownComponent, {
  DropdownPosition,
} from '../Dropdown/Dropdown.component';
import PortalComponent from '../Portal.component';
import DropdownItemComponent, {
  StyledDropdownItem,
  StyledDropdownItemLeft,
} from '../Dropdown/DropdownItem.component';
import { Transition } from 'react-transition-group';
import { useTranslation } from 'react-i18next';
import UserIcon from '../Icons/User.icon';
import LogoutIcon from '../Icons/Logout.icon';
import WrenchIcon from '../Icons/Wrench.icon';
import UserLogoutAlertComponent from '../Alert/User/UserLogoutAlert.component';
import '@intexsoft/wcm-dots';
import { useAppDispatch } from '../../hooks/useAppDispatcher.hook';
import { getNotificationCounterThunk } from '../../redux/Notification/NotificationThunk';
import { checkUserRequiredPermissions } from '../../utils/getUserRolesByToken';
import { UserPermissions } from '../../types/userPermissions.types';
import useAuthHook from '../../hooks/useAuth.hook';
import { useAppSelector } from '../../hooks/useAppSelector.hook';
import { getCandidateRecordsList } from '../../redux/Candidate/Record/Record.slice';
import { useNavigate } from 'react-router-dom';
import { RoutesConstant } from '../../constants/routes.constant';
import { getLocalStorage, setLocalStorage } from '../../utils/storage';

import dayjs from 'dayjs';
import { getSnowFall, setSnowFallAction } from '../../redux/SnowFall/SnowFall.slice';

const StyledLogoutDropdownItem = styled.div`
  ${StyledDropdownItem} {
    color: ${({ theme }) => theme.colors.RED.R400};

    ${StyledDropdownItemLeft} {
      color: ${({ theme }) => theme.colors.RED.R400};
    }
  }
`;
const StyledUserBar = styled.div`
  display: flex;
  padding: 14px 0;
  gap: 15px;
  justify-content: flex-end;

  ${StyledIconButton} {
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

    ${StyledBaseIcon} {
      color: ${({ theme }) => theme.colors.LABEL};
    }

    :hover {
      box-shadow: 0 3px 5px rgba(0, 0, 0, 0.1);

      ${StyledBaseIcon} {
        color: ${({ theme }) => theme.colors.BLUE.B400};
      }
    }
  }

  ${StyledAvatar} {
    cursor: pointer;
    box-shadow: 0 3px 5px rgba(0, 0, 0, 0.1);

    :hover {
      box-shadow: 0 3px 15px rgba(0, 0, 0, 0.2);
    }
  }
`;

const JIRA_HELP_CENTER_LINK =
  'https://jira.intexsoft.com/servicedesk/customer/portal/8/create/177';

const HeaderUserBarComponents = () => {
  const { user } = useAuthHook();
  const { t } = useTranslation();
  const theme = useTheme() as any;
  const dispatch = useAppDispatch();
  const { openModal } = useModalManager();
  const navigate = useNavigate();
  const kcUser = useSelector(getKCUser);
  const avatarRef = useRef(null);
  const [isOpenAvatarDropdown, setOpenAvatarDropdown] = useState(false);
  const notificationCounter = useSelector(getNotificationsCounter);
  const list = useAppSelector(getCandidateRecordsList); // ? зачем? может просто делать на каждый рендер?
  useEffect(() => {
    dispatch(getNotificationCounterThunk());
  }, [list]); // TODO: мы импортим лист только для этого... и как это связано?
  const handleOpenSearchModal = () => {
    openModal(GlobalSearchModal);
  };

  const snowfallActive = useAppSelector(getSnowFall);

  useEffect(() => {
    const storedActive = getLocalStorage('snowfallActive') === 'true';

    if (getLocalStorage('snowfallActive') === null) {
      dispatch(setSnowFallAction(true));
      setLocalStorage('snowfallActive', 'true');
    } else {
      dispatch(setSnowFallAction(storedActive));
      setLocalStorage('snowfallActive', JSON.stringify(snowfallActive));
    }
  }, []);

  const toggleSnowfall = () => {
    setLocalStorage('snowfallActive', JSON.stringify(!snowfallActive));
    dispatch(setSnowFallAction(!snowfallActive));
  };

  const handleNotificationModal = () => {
    openModal(
      NotificationsModalComponent,
      {
        isAccessDelete: true,
        isAccessReadAll: true,
      },
      {
        position: 'right-top',
      },
    );
  };

  const isWinter = [11, 0, 1].includes(dayjs().month());

  return (
    <StyledUserBar>
      {isWinter && (
      <IconButtonComponent mode="transparent" onClick={toggleSnowfall} className={'snow-toggle'}>
         <SnowIcon color={snowfallActive ? theme.colors.BLUE.B400 : theme.colors.TITLE} />
      </IconButtonComponent>
      )}
      <IconButtonComponent mode="transparent" onClick={handleOpenSearchModal}>
        <SearchIcon />
      </IconButtonComponent>
      {checkUserRequiredPermissions(user.permissions, [
        UserPermissions.CAN_VIEW_EVENT,
      ]) && (
        <IconButtonComponent
          mode="transparent"
          onClick={handleNotificationModal}
          badge={notificationCounter}
        >
          <BallIcon />
        </IconButtonComponent>
      )}
      <intexsoft-welcome-dots mode="light" />
      <AvatarComponent
        getRef={avatarRef}
        src={kcUser.email}
        gravatar
        handleClick={() => setOpenAvatarDropdown(true)}
      />
      <Transition
        in={isOpenAvatarDropdown}
        mountOnEnter={true}
        unmountOnExit={true}
        timeout={{
          exit: 300,
        }}
      >
        {state => (
          <PortalComponent
            withOverlay={false}
            onClose={() => setOpenAvatarDropdown(false)}
            position="manual"
            modalStyle={{
              ...theme.animations.defaultState,
              ...theme.animations.defaultSteps[state],
            }}
          >
            <DropdownComponent
              width="200px"
              position={DropdownPosition.LEFT_BOTTOM}
              getRef={avatarRef}
            >
              <DropdownItemComponent
                left={<UserIcon />}
                handleClick={() => navigate(RoutesConstant.PROFILE)}
              >
                {t('profile')}
              </DropdownItemComponent>
              <DropdownItemComponent
                left={<WrenchIcon />}
                handleClick={() => window.open(JIRA_HELP_CENTER_LINK, '_blank')}
              >
                {t('Support')}
              </DropdownItemComponent>
              <StyledLogoutDropdownItem>
                <DropdownItemComponent
                  left={<LogoutIcon />}
                  handleClick={() => openModal(UserLogoutAlertComponent)}
                >
                  {t('Log out')}
                </DropdownItemComponent>
              </StyledLogoutDropdownItem>
            </DropdownComponent>
          </PortalComponent>
        )}
      </Transition>
    </StyledUserBar>
  );
};

export default HeaderUserBarComponents;
